import { useRef, useState } from 'react'
import { TooltipProps } from './props'
import { StyledReference, StyledTooltip } from './styles'
import {
    arrow,
    autoPlacement as autoFunc,
    autoUpdate,
    flip as flipFunc,
    FloatingArrow,
    FloatingPortal,
    offset,
    shift as shiftFunc,
    useDelayGroup,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useId,
    useInteractions,
    useRole,
} from '@floating-ui/react'
import { config } from '../../Styles/stitches.config'

/**
 * Tooltip component used to display additional information when hovering over an element
 * @param {boolean} autoPlacement - Whether to automatically place the tooltip
 * @param {ReactElement} children - The element to which the tooltip is attached
 * @param {number} delay - The delay before the tooltip is shown
 * @param {boolean} flip - Whether to flip the tooltip when it reaches the edge of the screen
 * @param {object} maxSize - The maximum size of the tooltip
 * @param {string} tooltipText - The text to display in the tooltip
 *
 * @example
 * <Tooltip tooltipText="Tooltip text">
 *    <Text>Hover me</Text>
 * </Tooltip>
 */
const Tooltip = ({
    autoPlacement,
    children,
    delay = 0,
    flip,
    maxSize,
    placement,
    shift,
    tooltipContent,
}: TooltipProps) => {
    const arrowRef = useRef(null)
    const id = useId()

    const [isOpen, setIsOpen] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        middleware: [
            autoPlacement &&
                autoFunc(
                    typeof autoPlacement === 'boolean' ? {} : autoPlacement
                ),
            arrow({
                element: arrowRef,
            }),
            flip && flipFunc(typeof flip === 'boolean' ? {} : flip),
            offset(10),
            shift && shiftFunc(typeof shift === 'boolean' ? {} : shift),
        ],
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        placement,
    })

    const hover = useHover(context, { move: false, delay })
    const focus = useFocus(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        focus,
        dismiss,
        role,
    ])

    useDelayGroup(context, { id })

    return (
        <>
            <StyledReference ref={refs.setReference} {...getReferenceProps()}>
                {children}
            </StyledReference>
            {isOpen && (
                <FloatingPortal>
                    <StyledTooltip
                        ref={refs.setFloating}
                        style={floatingStyles}
                        css={{ maxH: maxSize?.height, maxW: maxSize?.width }}
                        {...getFloatingProps()}
                    >
                        <FloatingArrow
                            context={context}
                            fill={config.theme.colors.neutral600}
                            ref={arrowRef}
                        />
                        {tooltipContent}
                    </StyledTooltip>
                </FloatingPortal>
            )}
        </>
    )
}

export default Tooltip

export { StyledTooltip }
